import { useEffect } from "react";
import { useCompany } from "../../../contexts/CompanyContext";

export type AdminEventData = {
  headline: string;
  description: string;
  capDelta: number;
  repDelta: number;
  eventType: "opportunity" | "crisis";
  icon: string;
  dispatchedAt?: string;
};

interface Props {
  event: AdminEventData;
  onClose: () => void;
}

export default function AdminEventModal({ event, onClose }: Props) {
  const { updateCapital, updateReputation } = useCompany();
  const isOpportunity = event.eventType === "opportunity";

  useEffect(() => {
    const applyEffects = async () => {
      if (event.capDelta !== 0) await updateCapital(event.capDelta);
      if (event.repDelta !== 0) await updateReputation(event.repDelta);
    };
    applyEffects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.headline]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white border border-black/[0.08] rounded-2xl w-full max-w-md mx-4 overflow-hidden shadow-xl">

        {/* 강사 발송 배너 */}
        <div className="bg-[#4B6CF7]/[0.06] border-b border-[#4B6CF7]/15 px-5 py-2 flex items-center gap-2">
          <i className="ri-user-star-line text-[#4B6CF7]/70 text-xs"></i>
          <span className="text-[#4B6CF7]/80 text-[10px] font-medium">강사 발송 이벤트</span>
          {event.dispatchedAt && (
            <span className="text-[#BBBBBB] text-[10px] ml-auto">{event.dispatchedAt}</span>
          )}
        </div>

        {/* 이벤트 헤더 */}
        <div className={`px-6 pt-6 pb-4 border-b border-black/[0.07] ${
          isOpportunity
            ? "bg-gradient-to-b from-[#4B6CF7]/[0.04] to-transparent"
            : "bg-gradient-to-b from-red-50/60 to-transparent"
        }`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${
              isOpportunity
                ? "bg-[#4B6CF7]/10 border border-[#4B6CF7]/20"
                : "bg-red-50 border border-red-200/60"
            }`}>
              <i className={`${event.icon} text-xl ${isOpportunity ? "text-[#4B6CF7]" : "text-red-400"}`}></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${
                  isOpportunity
                    ? "text-[#4B6CF7] bg-[#4B6CF7]/[0.07] border-[#4B6CF7]/20"
                    : "text-red-500 bg-red-50 border-red-200"
                }`}>
                  {isOpportunity ? "기회 이벤트" : "위기 이벤트"}
                </span>
              </div>
              <h2 className="text-[#111111] font-semibold text-base">{event.headline}</h2>
            </div>
          </div>
        </div>

        {/* 이벤트 내용 */}
        <div className="px-6 py-4">
          <p className="text-[#666666] text-sm leading-relaxed mb-5">{event.description}</p>

          {/* 효과 표시 */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            {event.capDelta !== 0 && (
              <div className={`rounded-xl border p-3 text-center ${
                event.capDelta > 0
                  ? "bg-[#4B6CF7]/[0.06] border-[#4B6CF7]/15"
                  : "bg-red-50 border-red-100"
              }`}>
                <div className="text-[#BBBBBB] text-[9px] uppercase tracking-widest mb-1">자본금</div>
                <div className={`text-lg font-bold tabular-nums ${
                  event.capDelta > 0 ? "text-[#4B6CF7]" : "text-red-400"
                }`}>
                  {event.capDelta > 0 ? "+" : ""}{event.capDelta.toLocaleString()}만원
                </div>
              </div>
            )}
            {event.repDelta !== 0 && (
              <div className={`rounded-xl border p-3 text-center ${
                event.repDelta > 0
                  ? "bg-emerald-50 border-emerald-100"
                  : "bg-red-50 border-red-100"
              }`}>
                <div className="text-[#BBBBBB] text-[9px] uppercase tracking-widest mb-1">명성</div>
                <div className={`text-lg font-bold ${
                  event.repDelta > 0 ? "text-emerald-500" : "text-red-400"
                }`}>
                  {event.repDelta > 0 ? "+" : ""}{event.repDelta}pt
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap bg-[#4B6CF7] text-white hover:bg-[#5B7CF7]"
          >
            확인했습니다
          </button>
        </div>
      </div>
    </div>
  );
}
